import {
  WIX_CODE,
  DATA_BINDING,
  CMS,
  WIX_FORMS,
  FORM_APP,
  WIX_CMS,
} from '@wix/app-definition-ids'
import type { Scope } from '@sentry/browser'
import type {
  AppManifest,
  EditorPlatformApp,
  EditorReadyFn,
  EditorReadyOptions,
  PlatformEvent,
  HandleActionFn,
  OnEventFn,
} from '@wix/editor-platform-sdk-types'
import type {
  Features,
  AppApi,
  WixDataCreator,
  ModuleServices,
  CoreModule,
  DataBindingModule,
  SchemaManagementModule,
  DynamicPagesModule,
  WixDataConnectorSettings,
  ModuleEventHandler,
  EDITOR_TYPE,
} from '@wix/wix-data-client-common'
import {
  DATASET,
  APP_NAME,
  getFeatures,
  getDeveloperMode,
  listAllControllers,
  resolveEditorType,
  createCallFunctionWhenValueChanges,
  initializeDataSettings,
  enableDataMode,
  isDataModeEnabled,
  createCollectionItemCountsManager,
  isBlocks,
  syncRouters,
  setDynamicPageTypeStateForAllPages,
  isCategoryPage,
  isEditor3OrStudio2,
} from '@wix/wix-data-client-common'
import createAppManifest from './app-manifest'
import { getAppVersion } from '@wix/wix-data-client-common/dist/esm/infra/getAppVersion'
import { createCollectionsApi } from '@wix/wix-data-client-common/dist/esm/infra/createCollectionsApi'
import { createCollectionsTransportWorker } from './collections-transport/transport'
import { createSchemasClientAdapter } from './collections-transport/schemasClientAdapter'
import { decorateAdapterWithAppVisibilityControl } from './collections-transport/decorateAdapterWithAppVisibilityControl'
import createProxy from './proxy'
import {
  getActionsBySchema,
  updateAppStateActions,
} from './schemaContextMenuActions'
import assertUniqueMethods from './utils/assertUniqueMethods'
import createPublicApi from './publicApi'
import fixDynamicPages from './utils/fixDynamicPages'
import type { AppCollectionVisibilityPreferenceCreator } from './createAppCollectionVisibilityPreference'
import createAppCollectionVisibilityPreference from './createAppCollectionVisibilityPreference'
import setWorkerChunkBasePath from './setWorkerChunkBasePath'
import { uniq } from 'lodash'
import type {
  CollectionsTransport,
  CollectionsTransportWorker,
  DataSettingsClientConstructor,
  EditorSDK,
  FedopsLogger,
  GridAppId,
  I18n,
  RouterConfig,
  UnproxiedEditorSDK,
} from '@wix/wix-data-client-common-standalone'
import {
  getBiDefaults,
  createLogger,
  sentryAdapter,
  consoleLog,
  biAdapter,
  transformTranslations,
  createSafeUserPreferences,
  createSentry,
  createDeferred,
  createEditorSdkProxy,
  initEditorSDK,
} from '@wix/wix-data-client-common-standalone'
import { HttpClient } from '@wix/http-client'
import { setUpTemporaryLogStorage } from './logger/storage'
import * as transliteration from './transliteration'
import type { BindingDefinitionProviderCreator } from '@wix/wix-data-binding-definitions'
import { BindingDefinitionProvider } from '@wix/wix-data-binding-definitions'
import { removeStalePagelinks } from './utils/removeStalePagelinks'
import { getPremiumLimits } from './utils/getPremiumLimits'
import { getPremiumState } from './utils/getPremiumState'
import { getIsPremiumBusinessElite } from '@wix/cms-web-premium-helpers'
import {
  createEnterpriseBrandNameGetter,
  createSharedSchemasEnterpriseBrandNameGetter,
} from './utils/getEnterpriseBrandName'
import { removeStaleRoutes } from './utils/removeStaleRoutes'
import { onPremiumLimitsChange } from './duplexer/onPremiumLimitsChange'
import { onPageLinkGeneration } from './duplexer/onPageLinkGeneration'
import { onSchemaChange } from './duplexer/onSchemaChange'
import { onConvertToDynamicComplete } from './duplexer/onConvertToDynamicComplete'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type Experiments from '@wix/wix-experiments'
import type { SchemasClient as WixDataSchemasClient } from '@wix/wix-data-schemas-client'
import { onDataSettingsChange } from './data-settings/onDataSettingsChange'
import { createDataSettingsManager } from './data-settings/dataSettingsManager'
import { createDuplexerClient as _createDuplexerClient } from './duplexer/createDuplexerClient'
import { fixRouters } from './data-fixers/fixRouters'
import { addMissingSlugFields } from './data-fixers/addMissingSlugFields'
import { refreshRoutersForNewSlugs } from './utils/refreshRoutersForNewSlugs'
import { getWixCodeAuthParams } from './utils/getWixCodeAuthParams'

type WixDataSchemasCreator = (params: {
  httpClient: HttpClient
  instance: string
}) => WixDataSchemasClient

interface CreateWorkerParams {
  hostname: string
  i18nInstance: I18n
  i18nStudioInstance: I18n
  experimentsManager: Experiments
  fedopsLogger: FedopsLogger
  bindingDefinitionProviderCreator?: BindingDefinitionProviderCreator
  createDuplexerClient?: typeof _createDuplexerClient
  WixDataSettings: DataSettingsClientConstructor
  WixDataConnectorSettings: WixDataConnectorSettings
  createWixData: WixDataCreator
  createWixDataSchemas: WixDataSchemasCreator
  moduleExports: [
    coreModule: CoreModule,
    dataBindingModule: DataBindingModule,
    dynamicPagesModule: DynamicPagesModule,
    schemaManagementModule: SchemaManagementModule,
  ]
  setUpAppCollectionVisibilityPreference?: AppCollectionVisibilityPreferenceCreator
  Sentry?: Scope
  transliterateAndSlugify?: transliteration.TransliterateAndSlugify
  appVersion?: string
}

interface EditorReadyResult {
  t: I18n['t']
  locale: string
  onEventFn: ModuleEventHandler
  experimentsManager: Experiments
  editorSdkProxy: EditorSDK
  wixDataSchemasClient: WixDataSchemasClient
  collectionsTransport: CollectionsTransportWorker
  editorSDK: UnproxiedEditorSDK
  editorType: EDITOR_TYPE
  appApi: AppApi
  collectionsApi: CollectionsApi
  features: Features
}

type DataBindingPlatformApp = Omit<EditorPlatformApp, 'getAppManifest'> & {
  getAppManifest: () => Promise<AppManifest>
}

type InitialPrivateApi = Pick<AppApi, 'waitUntilReady'>

export default ({
  hostname,
  i18nInstance,
  i18nStudioInstance,
  experimentsManager: pendingExperimentsManager,
  fedopsLogger,
  bindingDefinitionProviderCreator = BindingDefinitionProvider,
  createDuplexerClient = _createDuplexerClient,
  WixDataSettings,
  WixDataConnectorSettings,
  createWixData,
  createWixDataSchemas,
  moduleExports,
  setUpAppCollectionVisibilityPreference = createAppCollectionVisibilityPreference,
  Sentry: initialSentry,
  transliterateAndSlugify = transliteration.transliterateAndSlugify,
  appVersion: initialAppVersion,
}: CreateWorkerParams): DataBindingPlatformApp => {
  const logger = createLogger()
  const temporaryLogStorage = setUpTemporaryLogStorage(logger)

  let editorReadyPromise: Promise<EditorReadyResult> | undefined
  let gridAppId: GridAppId | undefined

  const publicApi = createPublicApi(
    moduleExports.map(({ publicApi }) => publicApi).filter(Boolean),
  )

  const { promise: finalAppApiPromise, resolve: resolveAppApi } =
    createDeferred<AppApi>()

  const initialPrivateApi: InitialPrivateApi = {
    waitUntilReady: async () => {
      await finalAppApiPromise
    },
  }

  const collectionsTransportProxy = createProxy<CollectionsTransport>([
    'listSchemas',
    'loadSchema',
    'saveSchema',
    'removeSchema',
    'restoreSchema',
  ])
  const initTranslations = async function (
    locale: string,
    experimentsManager: Experiments,
  ) {
    await i18nInstance.init({ lng: locale })
    if (
      experimentsManager.enabled(
        'specs.wixDataClient.NewClassicBindingExperience',
      )
    ) {
      await i18nStudioInstance.init({ lng: locale })
      const inspectorResources = transformTranslations(
        i18nStudioInstance.getResourceBundle(locale, 'translation'),
        {
          prevPrefix: '{',
          prevSuffix: '}',
          newPrefix: '<%=',
          newSuffix: '%>',
        },
      )
      i18nInstance.addResourceBundle(locale, 'translation', inspectorResources)
    }
    return i18nInstance
  }

  const onEditorReady = async function (
    uninitializedEditorSDK: UnproxiedEditorSDK,
    appToken: string,
    editorEnv: EditorReadyOptions,
  ): Promise<EditorReadyResult> {
    try {
      const editorSDK = initEditorSDK(logger, uninitializedEditorSDK)
      const editorSdkProxy = createEditorSdkProxy(editorSDK, appToken)
      const editorType = await resolveEditorType({ editorEnv, editorSdkProxy })

      const appVersion =
        initialAppVersion || (await getAppVersion(editorSdkProxy))

      const Sentry = initialSentry
        ? initialSentry
        : createSentry({
            release: `${APP_NAME}@${appVersion}`,
          })
      logger.registerHandler(sentryAdapter(Sentry))
      logger.registerHandler(consoleLog())
      logger.registerHandler(biAdapter())

      let metaSiteId = await editorSdkProxy.info.getMetaSiteId()
      const userId = await editorSdkProxy.info.getUserId()
      const locale = await editorSdkProxy.environment.getLocale()
      logger.updateContext({
        tags: {
          msid: metaSiteId,
          resolvedEditorType: editorType,
          editorType: editorEnv.origin?.type,
          locale,
        },
        userId,
      })

      temporaryLogStorage.flush()

      const experimentsManager = await pendingExperimentsManager
        .ready()
        .then(() => pendingExperimentsManager)
      logger.updateContext({
        experiments: experimentsManager.all(),
      })

      const cmsAppData =
        await editorSdkProxy.document.tpa.app.getDataByAppDefId(WIX_CMS)
      const wixCodeAuthParams = await getWixCodeAuthParams({ editorSdkProxy })

      gridAppId = wixCodeAuthParams?.gridAppId
      const instance = cmsAppData
        ? cmsAppData.instance
        : wixCodeAuthParams?.instance

      if (!instance) {
        throw new Error('Failed to get instance for dbsm-editor-app worker')
      }

      const httpClient = new HttpClient({
        getAppToken: () => instance,
      })

      await fixRouters({ editorSdkProxy, logger })

      setDynamicPageTypeStateForAllPages({ editorSdkProxy })

      const wixDataSchemasClient = createWixDataSchemas({
        httpClient,
        instance,
      })

      const fetchCollectionIdsFromDatasets = async () => {
        const controllers = await listAllControllers(editorSdkProxy).then(
          connectableControllers =>
            Promise.all(
              (connectableControllers || []).map(controller =>
                controller?.controllerRef
                  ? editorSdkProxy.controllers.getData({
                      controllerRef: controller?.controllerRef,
                    })
                  : undefined,
              ),
            ),
        )
        const routers = await editorSdkProxy.routers.getAll<RouterConfig>()
        const datasetCollectionIds = controllers
          .filter(controller => controller?.type === DATASET)
          .map(controller => controller?.config?.dataset?.collectionName)
          .filter(id => !!id)
        const routerCollectionIds = routers
          .flatMap(router => Object.values(router?.config?.patterns || {}))
          .map(pattern => pattern?.config?.collection)
          .filter(id => !!id)
        const collectionIds = uniq(
          routerCollectionIds.concat(datasetCollectionIds),
        )
        return collectionIds
      }

      const adapter = createSchemasClientAdapter({
        wixDataSchemasClient,
        fetchCollectionIdsFromDatasets,
        syncRoutersPromise: isEditor3OrStudio2(editorType)
          ? syncRouters({
              editorSdkProxy,
              httpClient,
              routers: await editorSdkProxy.routers.getAll<RouterConfig>(),
            }).catch(cause => {
              logger.error(
                new Error('Failed to sync routers on worker load', { cause }),
              )
            })
          : undefined,
      })

      const getEnterpriseBrandName = createEnterpriseBrandNameGetter(httpClient)

      const collectionsTransport = createCollectionsTransportWorker({
        editorSDK,
        appToken,
        adapter: decorateAdapterWithAppVisibilityControl({
          logger,
          adapter,
          editorSdkProxy,
          finalAppApiPromise,
        }),
        finalAppApiPromise,
        getActionsBySchema: async ({ schemas }) =>
          await getActionsBySchema({
            editorSdkProxy,
            schemas,
            editorType,
            experimentsManager,
          }),
        getEnterpriseBrandName: createSharedSchemasEnterpriseBrandNameGetter(
          getEnterpriseBrandName,
          logger,
        ),
      })
      collectionsTransportProxy.setTarget(collectionsTransport.public)

      const i18n = await initTranslations(locale, experimentsManager)
      const t = i18n.t.bind(i18n)
      const collectionsApi = createCollectionsApi(
        editorSDK,
        appToken,
        collectionsTransport.public,
      )

      const duplexerClient = createDuplexerClient({
        getInstance: () => instance,
        logger,
      })

      const dataSettingsClient = new WixDataSettings(httpClient, {
        cache: true,
      })

      const dataSettingsManager = createDataSettingsManager({
        dataSettingsClient,
        duplexerClient,
      })

      const collectionItemCountsManager = createCollectionItemCountsManager({
        finalAppApiPromise,
        httpClient,
        collectionsApi,
        logger,
        getDataSettings: async () => dataSettingsManager.getDataSettings(),
        getGridAppId: async () => gridAppId,
      })

      dataSettingsManager.onSettingsChange(
        (previousDataSettings, nextDataSettings) =>
          onDataSettingsChange({
            previousDataSettings,
            nextDataSettings,
            logger,
            editorSdkProxy,
            httpClient,
            finalAppApiPromise,
            metaSiteId,
          }),
      )

      const isDevMode = await getDeveloperMode(editorSdkProxy)

      editorSdkProxy.editor.updateAppState({
        openDataPanelOnStart: true,
        isDevMode,
      })

      initializeDataSettings({ dataSettingsManager, logger }).then(() => {
        if (!isDataModeEnabled(dataSettingsManager) && isDevMode) {
          enableDataMode({ dataSettingsManager, logger })
        }
      })

      const wixData = await createWixData({
        httpClient,
        getGridAppId: async () => gridAppId,
      })
      const safeUserPreferences = createSafeUserPreferences({
        editorSdkProxy,
        fedopsLogger,
      })

      // @ts-expect-error todo
      const wixDataConnectorSettingsClient = new WixDataConnectorSettings(
        httpClient,
        instance,
      ) as WixDataConnectorSettings

      await setWorkerChunkBasePath(editorSdkProxy)

      const features = await getFeatures({ experimentsManager, editorType })

      logger.updateContext({
        biDefaults: {
          msid: () => metaSiteId,
          ...(await getBiDefaults({ appVersion, editorSdkProxy })),
        },
      })
      const bindingDefinitionProvider = await bindingDefinitionProviderCreator({
        locale,
        editorSdkProxy,
        experimentsManager,
        i18n,
        logger,
        features,
      })

      const moduleServices: ModuleServices = {
        i18n,
        logger,
        httpClient,
        editorSdkProxy,
        editorType,
        collectionsApi,
        collectionsTransport,
        fedopsLogger,
        experimentsManager,
        safeUserPreferences,
        wixData,
        hostname,
        dataSettingsManager,
        collectionItemCountsManager,
        bindingDefinitionProvider,
        wixDataConnectorSettingsClient,
        features,
        sdkVersion: editorSdkProxy.document.info.getSdkVersion().version,
        appCollectionVisibilityPreference:
          setUpAppCollectionVisibilityPreference({ dataSettingsManager }),
        transliterateAndSlugify,
        finalAppApiPromise,
        getEnterpriseBrandName,
        getInstance: () => instance,
        getGridAppId: () => gridAppId,
      }
      const moduleFactories = await Promise.all(
        moduleExports.map(({ createModuleFactories }) =>
          createModuleFactories(moduleServices),
        ),
      )

      const moduleAppApis = await Promise.all(
        moduleFactories.map(({ createAppApi }) =>
          createAppApi({ finalAppApiPromise }),
        ),
      )

      const appApi = buildAppApi(moduleAppApis, initialPrivateApi)

      await editorSdkProxy.editor.setAppAPI(appApi)
      resolveAppApi(appApi)

      publicApi.init({
        editorSDK,
        token: appToken,
        i18n,
        editorType,
        collectionsApi,
        wixData,
        safeUserPreferences,
        logger,
        appApi,
        hostname,
        experimentsManager,
        dataSettingsManager,
        httpClient,
        features,
      })

      updateAppStateActions({ editorSdkProxy, t })

      editorSDK.addEventListener('appUpdateCompleted', () => {
        appApi.refreshCollectionSchemasWhenAllowed()
      })

      if (!isBlocks(editorType)) {
        try {
          const [premiumLimits, premium, isPremiumBusinessElite] =
            await Promise.all([
              getPremiumLimits(httpClient),
              getPremiumState({ metaSiteId, httpClient }),
              getIsPremiumBusinessElite(httpClient, metaSiteId),
            ])
          editorSdkProxy.editor.updateAppState({
            premiumLimits,
            premium,
            isPremiumBusinessElite,
          })
        } catch (err) {
          logger.error(
            new Error(`Premium: failed to update app state`, { cause: err }),
          )
        }
      }
      const moduleEventHandlers = (await Promise.all(
        moduleFactories.map(async ({ getModuleName, createEventHandlers }) => {
          const handle = await createEventHandlers({ appApi })
          const moduleName = getModuleName()
          return async (event: PlatformEvent) => {
            try {
              await handle(event)
            } catch (error) {
              logger.error(
                new Error('onEvent handler failed', {
                  cause: error,
                }),
                { event, moduleName },
              )
            }
          }
        }),
      )) as ModuleEventHandler[]

      duplexerClient.onSchemaChange(() =>
        onSchemaChange(appApi, editorSdkProxy, features.pagesManifest),
      )
      duplexerClient.onPageLinkGeneration(payload =>
        onPageLinkGeneration(appApi, payload),
      )

      duplexerClient.onSlugValuesGenerated(({ collectionId, fields }) =>
        refreshRoutersForNewSlugs({
          editorSdkProxy,
          appApi,
          collectionId,
          fields,
        }),
      )

      duplexerClient.onConvertToDynamicComplete(payload =>
        onConvertToDynamicComplete(appApi, editorSdkProxy, payload),
      )

      if (!isBlocks(editorType)) {
        duplexerClient.onPremiumLimitsChange(async payload => {
          onPremiumLimitsChange(editorSdkProxy, payload)
        })
      }

      const siteIsSaved = await editorSdkProxy.info.isSiteSaved()
      if (!siteIsSaved) {
        appApi.addListener('siteWasFirstSaved', async () => {
          metaSiteId = await editorSdkProxy.info.getMetaSiteId()
          logger.updateContext({ tags: { msid: metaSiteId } })
        })
      }

      const onEventFn: ModuleEventHandler = async event => {
        moduleEventHandlers.forEach(handler => handler(event))
      }

      logger.breadcrumb({
        category: 'event',
        message: 'editorReady - done',
      })

      if (
        experimentsManager.enabled('specs.wixDataClient.RemoveStalePagelinks')
      ) {
        removeStalePagelinks({
          editorSdkProxy,
          collectionsApi,
          logger,
        }).catch(error =>
          logger.error(
            new Error(`Failed to remove stale pagelinks`, { cause: error }),
          ),
        )
      }
      if (experimentsManager.enabled('specs.wixDataClient.RemoveStaleRoutes')) {
        removeStaleRoutes({
          editorSdkProxy,
          logger,
        }).catch(error =>
          logger.error(
            new Error(`Failed to remove stale routes`, { cause: error }),
          ),
        )
      }

      if (isEditor3OrStudio2(editorType)) {
        await addMissingSlugFields({ editorSdkProxy, collectionsApi, logger })
      }

      return {
        t,
        locale,
        onEventFn,
        experimentsManager,
        editorSdkProxy,
        wixDataSchemasClient,
        collectionsTransport,
        editorSDK,
        editorType,
        appApi,
        collectionsApi,
        features,
      }
    } catch (e) {
      const error = new Error('Worker initialization failed', { cause: e })
      logger.error(error, {
        metaSiteId: await uninitializedEditorSDK.info
          .getMetaSiteId(appToken)
          .catch(() => {}),
      })
      throw error
    }
  }

  const editorReady: EditorReadyFn = (unproxiedEditorSdk, ...rest) => {
    logger.breadcrumb({
      category: 'event',
      message: 'editorReady',
    })
    if (editorReadyPromise) {
      return // editorReady called twice. ignoring
    }
    editorReadyPromise = onEditorReady(
      unproxiedEditorSdk as UnproxiedEditorSDK,
      ...rest,
    )
  }

  const getAppManifest = async function () {
    if (!editorReadyPromise) {
      throw new Error('getAppManifest called before editorReady')
    }
    try {
      const {
        t,
        experimentsManager,
        editorSdkProxy,
        editorType,
        collectionsApi,
        features,
        appApi,
      } = await editorReadyPromise

      let routers

      if (features.pagesManifest) {
        routers = await editorSdkProxy.routers.getAll()
        await fixDynamicPages(routers, editorSdkProxy.document.pages.data)
      }

      const bindingDefinitions = await appApi.getAllBindingDefinitions()

      return await createAppManifest({
        hostname,
        t,
        experimentsManager,
        editorSdkProxy,
        editorType,
        bindingDefinitions,
        collectionsApi,
        routers,
        features,
      })
    } catch (e) {
      const error = new Error(`Failed to create app manifest`, { cause: e })
      logger.error(error)
      throw error
    }
  }

  const getControllerPresets = async () => {
    return [
      {
        type: 'dataset',
        controllerConfig: {
          // initial data
        },
        serializedData: {
          name: 'Dataset (Unconfigured)',
        },
        uiDataForAddPanel: {
          name: 'Dataset',
        },
      },
    ]
  }

  const onEvent: OnEventFn = async function (event) {
    logger.breadcrumb({
      category: 'event',
      message: event.eventType,
      data: event.eventPayload,
    })
    if (!editorReadyPromise) {
      throw new Error('onEvent called before editorReady')
    }
    const { onEventFn } = await editorReadyPromise
    return onEventFn(event)
  }

  const callFunctionWhenValueChanges = createCallFunctionWhenValueChanges()
  const appInstallsToIgnore = [
    DATA_BINDING,
    WIX_CODE,
    WIX_FORMS,
    FORM_APP,
    CMS,
    WIX_CMS,
  ]

  const refreshCollectionSchemas = async () => {
    const { appApi, wixDataSchemasClient } = await editorReadyPromise!

    appApi.refreshCollectionSchemasWhenAllowed()
    callFunctionWhenValueChanges({
      pollTimes: 10,
      pollInterval: 3000,
      isEqual: (current, previous) =>
        current.length === previous.length &&
        current.every(id => previous.includes(id)),
      getValue: async () => Object.keys(await wixDataSchemasClient.list({})),
      fn: () => {
        appApi.refreshCollectionSchemasWhenAllowed()
      },
    })
  }

  const updateGridAppId = async () => {
    const { editorSdkProxy } = await editorReadyPromise!
    const wixCodeAuthParams = await getWixCodeAuthParams({ editorSdkProxy })
    gridAppId = wixCodeAuthParams?.gridAppId
  }

  const handleAction: HandleActionFn = async ({ type, payload }) => {
    switch (type) {
      case 'appInstalled': {
        const { appDefinitionId } = payload as { appDefinitionId: string }
        if (appDefinitionId === WIX_CODE) {
          await updateGridAppId()
        }
        if (!appInstallsToIgnore.includes(appDefinitionId)) {
          await refreshCollectionSchemas()
        }
        break
      }
      case 'removeAppCompleted': {
        const { appDefinitionId } = payload as { appDefinitionId: string }
        if (!appInstallsToIgnore.includes(appDefinitionId)) {
          await refreshCollectionSchemas()
        }
        break
      }
      default:
        break
    }
  }

  return {
    editorReady,
    handleAction,
    exports: () => ({
      private: { ...initialPrivateApi },
      public: {
        collectionsTransport: collectionsTransportProxy.proxy,
        ...collectionsTransportProxy.proxy,
        ...publicApi.api,
      },
      editor: {
        getDynamicPageNavBarConfig: async () => {
          if (!editorReadyPromise) {
            return {}
          }

          const { t, editorSdkProxy, collectionsApi } = await editorReadyPromise
          const isOnCategoryPage = await isCategoryPage({
            editorSdkProxy,
            collectionsApi,
          })

          return {
            navBarMainAction: {
              label: isOnCategoryPage
                ? t('Dynamic_Pages_Nav_Bar_Main_Action_Edit_Category_Label')
                : t('Dynamic_Pages_Nav_Bar_Main_Action_Label'),
              actionId: 'openContentManagerInTableView',
            },
          }
        },
      },
    }),
    getAppManifest,
    // @ts-expect-error TODO - is this needed? Not in EditorPlatformApp type and also not documented https://dev.wix.com/docs/fed-guild/articles/editor-platform/editor-application-reference/editor-platform-app
    getControllerPresets,
    onEvent,
  }
}

// TODO: typing
function buildAppApi(moduleAppApis: any[], initialApi: InitialPrivateApi) {
  const appApi = moduleAppApis.reduce((api, moduleApi) => {
    assertUniqueMethods(api, moduleApi)
    return { ...api, ...moduleApi }
  }, initialApi) as AppApi
  for (const [methodName, method] of Object.entries(appApi)) {
    // @ts-expect-error TODO typing
    appApi[methodName] = method.bind(appApi)
  }

  return appApi as AppApi
}
